import * as THREE from 'three'
import { logger } from '@/app/logger'
import { CanonicalCut, Inequality } from '../core/cuts'
import { ConvexGeometry } from 'three/examples/jsm/geometries/ConvexGeometry.js'

/**
 * Geometric utilities for converting mathematical concepts to Three.js objects
 * Handles plane creation, polyhedron representation, and coordinate transforms
 */

/**
 * Convert an inequality alpha·x <= beta to a THREE.Plane
 */
export function createPlaneFromInequality(alpha: number[], beta: number): THREE.Plane {
  // Inequality: alpha[0]*x + alpha[1]*y + alpha[2]*z <= beta
  // Plane equation: alpha[0]*x + alpha[1]*y + alpha[2]*z = beta
  // THREE.Plane equation: normal·point + constant = 0
  // So: alpha[0]*x + alpha[1]*y + alpha[2]*z - beta = 0
  // Therefore: normal = [alpha[0], alpha[1], alpha[2]], constant = -beta
  const normal = new THREE.Vector3(alpha[0], alpha[1], alpha[2]);
  const length = normal.length();
  if (length === 0) {
    normal.set(0, 0, 1);
    return new THREE.Plane(normal, -beta);
  }
  normal.divideScalar(length);
  return new THREE.Plane(normal, -beta / length);
}

/**
 * Create a visual representation of a cutting plane
 * Returns a large plane mesh that can be used for visualization
 */
export function createCuttingPlaneVisualization(
  alpha: number[],
  beta: number,
  size: number = 10,
  color: number = 0x3b82f6,  // Professional blue
  opacity: number = 0.25
): THREE.Mesh {
  try {
    // Validate input parameters
    if (!alpha || alpha.length < 3) {
      logger.error('Invalid alpha parameter for cutting plane:', alpha);
      throw new Error('Alpha must have at least 3 components');
    }

    if (typeof beta !== 'number' || !isFinite(beta)) {
      logger.error('Invalid beta parameter for cutting plane:', beta);
      throw new Error('Beta must be a finite number');
    }

    const plane = createPlaneFromInequality(alpha, beta);

    // Create a smaller plane geometry for better visibility
    const geometry = new THREE.PlaneGeometry(size * 1.0, size * 1.0);

    // Create modern, elegant material with better transparency
    const material = new THREE.MeshStandardMaterial({
      color: color,
      transparent: true,
      opacity: opacity,
      side: THREE.DoubleSide,
      depthWrite: false,
      depthTest: true,
      alphaTest: 0.1,
      metalness: 0.1,
      roughness: 0.4,
      envMapIntensity: 0.3,
      fog: false,  // Disable fog effect on cutting planes
      // Add subtle glow effect
      emissive: new THREE.Color(color).multiplyScalar(0.1),
      emissiveIntensity: 0.2
    });

    const planeMesh = new THREE.Mesh(geometry, material);

    // Set higher render order to appear above polyhedron
    planeMesh.renderOrder = 50;

    // Position and orient the plane mesh
    // The plane normal gives us the orientation
    const normal = plane.normal.clone();

    // Validate normal vector
    if (!normal || normal.length() === 0) {
      logger.error('Invalid normal vector for cutting plane:', normal);
      // Create fallback normal
      normal.set(alpha[0] || 0, alpha[1] || 0, alpha[2] || 0).normalize();
    }

    // Fix position calculation:
    // The THREE.Plane is defined as normal.dot(point) + constant = 0
    // Since normal is normalized, the signed distance from origin to plane is -constant.
    // The normal points in the direction of the coefficients (alpha).
    // So we position the mesh center at normal * (-constant).

    const normalizedNormal = plane.normal.clone();
    const distanceToOrigin = -plane.constant;

    // Position the plane at the correct distance along the normal vector
    planeMesh.position.copy(normalizedNormal.multiplyScalar(distanceToOrigin));

    // Store the normalized normal in userData for animation
    planeMesh.userData.normal = normalizedNormal.clone();

    // Orient the plane to face the correct direction
    planeMesh.lookAt(
      planeMesh.position.x + normalizedNormal.x,
      planeMesh.position.y + normalizedNormal.y,
      planeMesh.position.z + normalizedNormal.z
    );

    return planeMesh;
  } catch (error) {
    logger.error('Error creating cutting plane visualization:', error);

    // Return a fallback plane mesh with modern styling
    const fallbackGeometry = new THREE.PlaneGeometry(size, size);
    const fallbackMaterial = new THREE.MeshStandardMaterial({
      color: 0x64b5f6,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide,
      emissive: new THREE.Color(0x64b5f6).multiplyScalar(0.1)
    });

    const fallbackMesh = new THREE.Mesh(fallbackGeometry, fallbackMaterial);
    fallbackMesh.position.set(0, 0, 0);
    fallbackMesh.userData.normal = new THREE.Vector3(0, 0, 1);

    return fallbackMesh;
  }
}

/**
 * Create wireframe representation of a cutting plane
 */
export function createCuttingPlaneWireframe(
  alpha: number[],
  beta: number,
  size: number = 10,
  color: number = 0x1d4ed8  // Professional blue, slightly darker than the plane
): THREE.LineSegments {
  // Create a simple plane outline instead of full wireframe to avoid diagonal lines
  const geometry = new THREE.PlaneGeometry(size * 1.0, size * 1.0, 1, 1);

  // Create edge geometry instead of wireframe to avoid diagonal lines
  const edges = new THREE.EdgesGeometry(geometry);
  const wireframeMaterial = new THREE.LineBasicMaterial({
    color: color,
    linewidth: 2,
    transparent: true,
    opacity: 0.6,
    fog: false  // Disable fog effect on wireframe
  });

  const wireframe = new THREE.LineSegments(edges, wireframeMaterial);

  // Set render order for proper layering above cutting plane
  wireframe.renderOrder = 60; // Render on top of cutting plane

  // Position the wireframe same as the plane
  const planeMesh = createCuttingPlaneVisualization(alpha, beta, size, color, 1.0);
  wireframe.position.copy(planeMesh.position);
  wireframe.rotation.copy(planeMesh.rotation);

  return wireframe;
}

/**
 * Create a simple polyhedron with minimal triangulation from inequalities
 * Each face will be a simple polygon without unnecessary subdivision
 */
export function createSimplePolyhedronFromInequalities(
  inequalities: Inequality[]
): THREE.BufferGeometry {
  if (!inequalities || inequalities.length < 3) {
    logger.warn('Need at least 3 constraints for 3D polyhedron');
    return new THREE.BufferGeometry();
  }

  // Step 1: Generate all valid vertices
  const vertices = generateVerticesFromConstraints(inequalities);

  if (vertices.length < 4) {
    logger.warn('Not enough valid vertices for full 3D polyhedron');

    // --- 0D case: single vertex -> small sphere around the point ---
    if (vertices.length === 1) {
      const v0 = vertices[0];
      const bounds = computeAABBFromInequalities(inequalities);
      const spanX = Math.max(0.1, bounds.xMax - bounds.xMin);
      const spanY = Math.max(0.1, bounds.yMax - bounds.yMin);
      const spanZ = Math.max(0.1, bounds.zMax - bounds.zMin);
      const radius = 0.05 * Math.max(spanX, spanY, spanZ);

      const sphere = new THREE.SphereGeometry(radius, 16, 16);
      sphere.translate(v0.x, v0.y, v0.z);
      sphere.computeBoundingBox();
      sphere.computeVertexNormals();
      return sphere;
    }

    // --- 1D case: two or more colinear vertices -> thin cylinder segment ---
    if (vertices.length === 2) {
      const [v0, v1] = vertices;
      const dir = new THREE.Vector3().subVectors(v1, v0);
      const length = dir.length();
      if (length > 1e-8) {
        const bounds = computeAABBFromInequalities(inequalities);
        const spanX = Math.max(0.1, bounds.xMax - bounds.xMin);
        const spanY = Math.max(0.1, bounds.yMax - bounds.yMin);
        const spanZ = Math.max(0.1, bounds.zMax - bounds.zMin);
        const radius = 0.02 * Math.max(spanX, spanY, spanZ);

        const cyl = new THREE.CylinderGeometry(radius, radius, length, 12);
        // By default, cylinder is along Y-axis from (-len/2, len/2)
        cyl.translate(0, length / 2, 0);

        const m = new THREE.Matrix4();
        const quat = new THREE.Quaternion();
        const up = new THREE.Vector3(0, 1, 0);
        const dirNorm = dir.clone().normalize();
        quat.setFromUnitVectors(up, dirNorm);
        m.makeRotationFromQuaternion(quat);
        cyl.applyMatrix4(m);

        const mid = new THREE.Vector3().addVectors(v0, v1).multiplyScalar(0.5);
        cyl.translate(mid.x, mid.y, mid.z);

        cyl.computeBoundingBox();
        cyl.computeVertexNormals();
        return cyl;
      }
    }

    if (vertices.length === 3) {
      const v0 = vertices[0];
      const v1 = vertices[1];
      const v2 = vertices[2];

      const e1 = new THREE.Vector3().subVectors(v1, v0);
      const e2 = new THREE.Vector3().subVectors(v2, v0);
      const cross = new THREE.Vector3().crossVectors(e1, e2);

      if (cross.lengthSq() > 1e-8) {
        // --- 2D case: three non-colinear vertices -> single triangle ---
        const geometry = new THREE.BufferGeometry();
        const positions = new Float32Array([
          v0.x, v0.y, v0.z,
          v1.x, v1.y, v1.z,
          v2.x, v2.y, v2.z,
        ]);
        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setIndex([0, 1, 2]);
        geometry.computeVertexNormals();
        geometry.computeBoundingBox();
        return geometry;
      } else {
        // Three nearly colinear vertices -> treat as 1D and render cylinder between extremes
        const projAxis = e1.lengthSq() > e2.lengthSq() ? e1.clone() : e2.clone();
        if (projAxis.lengthSq() > 1e-8) {
          const axis = projAxis.normalize();
          let tMin = Infinity;
          let tMax = -Infinity;
          let pMin = v0.clone();
          let pMax = v0.clone();
          vertices.forEach((v) => {
            const t = axis.dot(new THREE.Vector3().subVectors(v, v0));
            if (t < tMin) {
              tMin = t;
              pMin = v.clone();
            }
            if (t > tMax) {
              tMax = t;
              pMax = v.clone();
            }
          });

          const bounds = computeAABBFromInequalities(inequalities);
          const spanX = Math.max(0.1, bounds.xMax - bounds.xMin);
          const spanY = Math.max(0.1, bounds.yMax - bounds.yMin);
          const spanZ = Math.max(0.1, bounds.zMax - bounds.zMin);
          const radius = 0.02 * Math.max(spanX, spanY, spanZ);

          const segDir = new THREE.Vector3().subVectors(pMax, pMin);
          const segLen = segDir.length();
          if (segLen > 1e-8) {
            const cyl = new THREE.CylinderGeometry(radius, radius, segLen, 12);
            cyl.translate(0, segLen / 2, 0);

            const m = new THREE.Matrix4();
            const quat = new THREE.Quaternion();
            const up = new THREE.Vector3(0, 1, 0);
            const segDirNorm = segDir.clone().normalize();
            quat.setFromUnitVectors(up, segDirNorm);
            m.makeRotationFromQuaternion(quat);
            cyl.applyMatrix4(m);

            const mid = new THREE.Vector3().addVectors(pMin, pMax).multiplyScalar(0.5);
            cyl.translate(mid.x, mid.y, mid.z);

            cyl.computeBoundingBox();
            cyl.computeVertexNormals();
            return cyl;
          }
        }
      }
    }

    // Generic fallback: axis-aligned bounding box visualization.
    // If we cannot even obtain a finite AABB (e.g. empty or wildly
    // ill-conditioned region so computeAABBFromInequalities returns
    // hasFinite=false), we prefer to return an invisible placeholder
    // rather than a misleading huge cube.
    const bounds = computeAABBFromInequalities(inequalities);
    if (!bounds.hasFinite) {
      const g = new THREE.BufferGeometry();
      return g;
    }
    const size = {
      x: Math.max(0.1, bounds.xMax - bounds.xMin),
      y: Math.max(0.1, bounds.yMax - bounds.yMin),
      z: Math.max(0.1, bounds.zMax - bounds.zMin),
    };
    const geometry = new THREE.BoxGeometry(size.x, size.y, size.z);
    geometry.translate(
      (bounds.xMin + bounds.xMax) / 2,
      (bounds.yMin + bounds.yMax) / 2,
      (bounds.zMin + bounds.zMax) / 2,
    );
    geometry.computeBoundingBox();
    geometry.computeVertexNormals();
    return geometry;
  }

  // Step 2: Build faces for each constraint
  const positions: number[] = [];
  const indices: number[] = [];

  // Add all vertices to positions array
  vertices.forEach((vertex) => {
    positions.push(vertex.x, vertex.y, vertex.z);
  });

  // For each constraint, find vertices that lie on its boundary and create a face
  inequalities.forEach((constraint) => {
    const alpha3D = ensureThreeDimensions(constraint.alpha);
    const normal = new THREE.Vector3(alpha3D[0], alpha3D[1], alpha3D[2]).normalize();

    // Find vertices that lie on this constraint's boundary
    const faceVertices: { vertex: THREE.Vector3; index: number }[] = [];

    vertices.forEach((vertex, index) => {
      const value = alpha3D[0] * vertex.x + alpha3D[1] * vertex.y + alpha3D[2] * vertex.z;
      if (Math.abs(value - constraint.beta) < 1e-6) {
        faceVertices.push({ vertex, index });
      }
    });

    if (faceVertices.length >= 3) {
      // Sort vertices in counter-clockwise order around the face normal
      const center = faceVertices.reduce(
        (sum, fv) => sum.add(fv.vertex.clone()),
        new THREE.Vector3()
      ).divideScalar(faceVertices.length);

      // Create reference vectors for sorting
      const firstVec = new THREE.Vector3().subVectors(faceVertices[0].vertex, center).normalize();
      const tangent = firstVec.clone();
      const binormal = normal.clone().cross(tangent).normalize();

      faceVertices.sort((a, b) => {
        const vecA = new THREE.Vector3().subVectors(a.vertex, center);
        const vecB = new THREE.Vector3().subVectors(b.vertex, center);

        const angleA = Math.atan2(vecA.dot(binormal), vecA.dot(tangent));
        const angleB = Math.atan2(vecB.dot(binormal), vecB.dot(tangent));

        return angleA - angleB;
      });

      // Create triangular faces for this polygon (fan triangulation from first vertex)
      for (let i = 1; i < faceVertices.length - 1; i++) {
        const v0 = faceVertices[0].index;
        const v1 = faceVertices[i].index;
        const v2 = faceVertices[i + 1].index;

        // Add triangle indices
        indices.push(v0, v1, v2);
      }
    }
  });

  // Create BufferGeometry
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);

  // Compute vertex normals automatically
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();

  logger.log(`Created simple polyhedron with ${positions.length / 3} vertices and ${indices.length / 3} triangles`);

  return geometry;
}

/**
 * Generate convex hull from constraints using vertex enumeration approach.
 * Algorithm:
 * 1. Generate all C(m,3) combinations of constraints
 * 2. Solve 3x3 linear system for each combination
 * 3. Keep only vertices that satisfy ALL constraints
 * 4. Use ConvexGeometry to generate convex hull
 */
export function createPolyhedronFromInequalities(
  inequalities: Inequality[]
): THREE.BufferGeometry {
  // Robust approach: enumerate valid vertices, then build convex hull.
  // This removes internal triangulation artifacts and guarantees a closed convex polyhedron.
  const vertices = generateVerticesFromConstraints(inequalities)
  if (vertices.length < 4) {
    const bounds = computeAABBFromInequalities(inequalities)
    const integerSeeds = findIntegerPointsInPolyhedron(inequalities, {
      xMin: bounds.xMin, xMax: bounds.xMax,
      yMin: bounds.yMin, yMax: bounds.yMax,
      zMin: bounds.zMin, zMax: bounds.zMax
    })
    if (integerSeeds.length >= 4) {
      const hull = new ConvexGeometry(integerSeeds)
      hull.computeVertexNormals()
      hull.computeBoundingBox()
      return hull
    }
    // Fallback to simple approach when not enough vertices or seeds
    return createSimplePolyhedronFromInequalities(inequalities)
  }
  const hull = new ConvexGeometry(vertices)
  hull.computeVertexNormals()
  hull.computeBoundingBox()
  return hull
}

/**
 * Generate all valid vertices from constraints using C(m,3) combinations
 */
export function generateVerticesFromConstraints(inequalities: Inequality[]): THREE.Vector3[] {
  const vertices: THREE.Vector3[] = [];
  const m = inequalities.length;

  // Generate all C(m,3) combinations
  for (let i = 0; i < m; i++) {
    for (let j = i + 1; j < m; j++) {
      for (let k = j + 1; k < m; k++) {
        // Get three constraints
        const c1 = inequalities[i];
        const c2 = inequalities[j];
        const c3 = inequalities[k];

        // Convert to 3D if needed
        const alpha1 = ensureThreeDimensions(c1.alpha);
        const alpha2 = ensureThreeDimensions(c2.alpha);
        const alpha3 = ensureThreeDimensions(c3.alpha);

        // Solve 3x3 linear system: A * x = b
        const A = [
          [alpha1[0], alpha1[1], alpha1[2]],
          [alpha2[0], alpha2[1], alpha2[2]],
          [alpha3[0], alpha3[1], alpha3[2]]
        ];
        const b = [c1.beta, c2.beta, c3.beta];

        const vertex = solveLinearSystem3x3(A, b);

        if (vertex && isValidVertex(vertex, inequalities)) {
          vertices.push(vertex);
        }
      }
    }
  }

  return removeDuplicateVertices(vertices);
}

/**
 * Ensure constraint has 3 dimensions (extend with 0 if needed)
 */
function ensureThreeDimensions(alpha: number[]): number[] {
  if (alpha.length >= 3) {
    return alpha.slice(0, 3);
  }
  const result = [...alpha];
  while (result.length < 3) {
    result.push(0);
  }
  return result;
}

/**
 * Solve 3x3 linear system A * x = b using Gaussian elimination
 * Returns null if no unique solution (matrix not full rank)
 */
function solveLinearSystem3x3(A: number[][], b: number[]): THREE.Vector3 | null {
  // Create augmented matrix [A|b]
  const augmented = A.map((row, i) => [...row, b[i]]);

  // Gaussian elimination with partial pivoting
  for (let i = 0; i < 3; i++) {
    // Find pivot
    let maxRow = i;
    for (let k = i + 1; k < 3; k++) {
      if (Math.abs(augmented[k][i]) > Math.abs(augmented[maxRow][i])) {
        maxRow = k;
      }
    }

    // Swap rows
    [augmented[i], augmented[maxRow]] = [augmented[maxRow], augmented[i]];

    // Check if matrix is singular (rank < 3)
    if (Math.abs(augmented[i][i]) < 1e-10) {
      return null; // No unique solution
    }

    // Eliminate below pivot
    for (let k = i + 1; k < 3; k++) {
      const factor = augmented[k][i] / augmented[i][i];
      for (let j = i; j < 4; j++) {
        augmented[k][j] -= factor * augmented[i][j];
      }
    }
  }

  // Back substitution
  const x = new Array(3);
  for (let i = 2; i >= 0; i--) {
    x[i] = augmented[i][3];
    for (let j = i + 1; j < 3; j++) {
      x[i] -= augmented[i][j] * x[j];
    }
    x[i] /= augmented[i][i];
  }

  // Check for numerical issues
  if (x.some(val => !isFinite(val))) {
    return null;
  }

  return new THREE.Vector3(x[0], x[1], x[2]);
}

/**
 * Check if vertex satisfies ALL constraints
 */
function isValidVertex(vertex: THREE.Vector3, inequalities: Inequality[]): boolean {
  for (const ineq of inequalities) {
    const alpha3D = ensureThreeDimensions(ineq.alpha);
    const value = alpha3D[0] * vertex.x + alpha3D[1] * vertex.y + alpha3D[2] * vertex.z;

    // Check constraint: alpha^T * x <= beta (with small tolerance)
    if (value > ineq.beta + 1e-6) {
      return false;
    }
  }
  return true;
}

/**
 * Remove duplicate vertices (within tolerance)
 */
function removeDuplicateVertices(vertices: THREE.Vector3[], tolerance: number = 1e-6): THREE.Vector3[] {
  const unique: THREE.Vector3[] = [];

  for (const vertex of vertices) {
    let isDuplicate = false;
    for (const existing of unique) {
      if (vertex.distanceTo(existing) < tolerance) {
        isDuplicate = true;
        break;
      }
    }
    if (!isDuplicate) {
      unique.push(vertex);
    }
  }

  return unique;
}



/**
 * Check if a point is inside a polyhedron defined by linear inequalities
 */
export function isPointInsidePolyhedron(
  point: THREE.Vector3,
  inequalities: { alpha: number[], beta: number }[]
): boolean {
  for (const inequality of inequalities) {
    const { alpha, beta } = inequality
    const value = alpha[0] * point.x + alpha[1] * point.y + alpha[2] * point.z
    if (value > beta + 1e-6) { // Small tolerance
      return false
    }
  }
  return true
}

/**
 * Find integer points inside a polyhedron
 */
export function findIntegerPointsInPolyhedron(
  inequalities: { alpha: number[], beta: number }[],
  searchBounds: { min: number, max: number } | { xMin: number, xMax: number, yMin: number, yMax: number, zMin: number, zMax: number } = { min: -1, max: 20 }
): THREE.Vector3[] {
  const integerPoints: THREE.Vector3[] = []
  const MAX_POINTS = 200000

  const hasPerAxis = (searchBounds as any).xMin !== undefined
  const xMin = hasPerAxis ? (searchBounds as any).xMin : (searchBounds as any).min
  const xMax = hasPerAxis ? (searchBounds as any).xMax : (searchBounds as any).max
  const yMin = hasPerAxis ? (searchBounds as any).yMin : (searchBounds as any).min
  const yMax = hasPerAxis ? (searchBounds as any).yMax : (searchBounds as any).max
  const zMin = hasPerAxis ? (searchBounds as any).zMin : (searchBounds as any).min
  const zMax = hasPerAxis ? (searchBounds as any).zMax : (searchBounds as any).max

  const spanX = xMax - xMin + 1
  const spanY = yMax - yMin + 1
  const spanZ = zMax - zMin + 1
  const totalPoints = spanX * spanY * spanZ

  if (totalPoints > MAX_POINTS) {
    const targetSpan = Math.max(1, Math.floor(Math.cbrt(MAX_POINTS)))
    const shrinkAxis = (min: number, max: number) => {
      const center = Math.floor((min + max) / 2)
      const half = Math.floor((targetSpan - 1) / 2)
      let newMin = Math.max(min, center - half)
      let newMax = Math.min(max, newMin + targetSpan - 1)
      if (newMax - newMin + 1 < targetSpan && newMax < max) {
        const deficit = targetSpan - (newMax - newMin + 1)
        newMax = Math.min(max, newMax + deficit)
      }
      return { min: newMin, max: newMax }
    }

    const nx = shrinkAxis(xMin, xMax)
    const ny = shrinkAxis(yMin, yMax)
    const nz = shrinkAxis(zMin, zMax)

    return findIntegerPointsInPolyhedron(inequalities, {
      xMin: nx.min, xMax: nx.max,
      yMin: ny.min, yMax: ny.max,
      zMin: nz.min, zMax: nz.max
    })
  }

  for (let x = xMin; x <= xMax; x++) {
    for (let y = yMin; y <= yMax; y++) {
      for (let z = zMin; z <= zMax; z++) {
        const point = new THREE.Vector3(x, y, z)
        if (isPointInsidePolyhedron(point, inequalities)) {
          integerPoints.push(point)
        }
      }
    }
  }

  return integerPoints
}

/**
 * Compute axis-aligned bounding box [min,max] for x,y,z from feasible vertices.
 * Equivalent于对每个坐标单独求 LP 极值，但通过已存在的顶点枚举实现，数值稳定且复用几何管线。
 */
export function computeAABBFromInequalities(inequalities: Inequality[]): {
  xMin: number, xMax: number,
  yMin: number, yMax: number,
  zMin: number, zMax: number,
  hasFinite: boolean
} {
  const margin = 1
  const nodeRequire = (globalThis as any).require as ((path: string) => any) | undefined

  const solveBoundsWithSimplex = () => {
    if (typeof nodeRequire !== 'function') return null
    try {
      const { SimplexSolver } = nodeRequire('../core/simplex') as { SimplexSolver?: any }
      if (!SimplexSolver) return null
      const A = inequalities.map((ineq) => ensureThreeDimensions(ineq.alpha))
      const b = inequalities.map((ineq) => ineq.beta)

      const solve = (c: number[]) => {
        const result = SimplexSolver.getOptimalTableau(A, b, c)
        if (!result.isOptimal) return null
        const val = result.objectiveValue.toNumber()
        return Number.isFinite(val) ? val : null
      }

      const maxX = solve([1, 0, 0])
      const minX = solve([-1, 0, 0])
      const maxY = solve([0, 1, 0])
      const minY = solve([0, -1, 0])
      const maxZ = solve([0, 0, 1])
      const minZ = solve([0, 0, -1])

      if ([maxX, minX, maxY, minY, maxZ, minZ].some((v) => v === null)) {
        return null
      }

      return {
        xMin: -(minX as number),
        xMax: maxX as number,
        yMin: -(minY as number),
        yMax: maxY as number,
        zMin: -(minZ as number),
        zMax: maxZ as number,
      }
    } catch {
      return null
    }
  }

  const simplexBounds = solveBoundsWithSimplex()
  if (simplexBounds) {
    return {
      xMin: Math.floor(simplexBounds.xMin) - margin,
      xMax: Math.ceil(simplexBounds.xMax) + margin,
      yMin: Math.floor(simplexBounds.yMin) - margin,
      yMax: Math.ceil(simplexBounds.yMax) + margin,
      zMin: Math.floor(simplexBounds.zMin) - margin,
      zMax: Math.ceil(simplexBounds.zMax) + margin,
      hasFinite: true,
    }
  }

  const verts = generateVerticesFromConstraints(inequalities)
  if (verts.length === 0) {
    return { xMin: -4, xMax: 4, yMin: -4, yMax: 4, zMin: -4, zMax: 4, hasFinite: false }
  }
  let xmin = Infinity, xmax = -Infinity
  let ymin = Infinity, ymax = -Infinity
  let zmin = Infinity, zmax = -Infinity
  for (const v of verts) {
    if (v.x < xmin) xmin = v.x
    if (v.x > xmax) xmax = v.x
    if (v.y < ymin) ymin = v.y
    if (v.y > ymax) ymax = v.y
    if (v.z < zmin) zmin = v.z
    if (v.z > zmax) zmax = v.z
  }
  return {
    xMin: Math.floor(xmin) - margin,
    xMax: Math.ceil(xmax) + margin,
    yMin: Math.floor(ymin) - margin,
    yMax: Math.ceil(ymax) + margin,
    zMin: Math.floor(zmin) - margin,
    zMax: Math.ceil(zmax) + margin,
    hasFinite: isFinite(xmin) && isFinite(xmax) && isFinite(ymin) && isFinite(ymax) && isFinite(zmin) && isFinite(zmax)
  }
}

/**
 * Calculate the volume of a simple polyhedron (approximate)
 */
export function approximatePolyhedronVolume(geometry: THREE.BufferGeometry): number {
  // Simple approximation using bounding box
  geometry.computeBoundingBox()
  const boundingBox = geometry.boundingBox

  if (!boundingBox) {
    return 0
  }

  const size = boundingBox.getSize(new THREE.Vector3())
  return size.x * size.y * size.z
}

/**
 * Create a cutting plane from a CanonicalCut object
 */
export function createCuttingPlaneFromCut(
  cut: CanonicalCut,
  size: number = 10,
  animated: boolean = false
): {
  plane: THREE.Plane,
  visualization: THREE.Mesh,
  wireframe: THREE.LineSegments
} {
  try {
    // Validate input
    if (!cut || !cut.alpha || !Array.isArray(cut.alpha)) {
      logger.error('Invalid cut object:', cut);
      throw new Error('Invalid cut object: missing or invalid alpha');
    }

    if (typeof cut.beta !== 'number' || !isFinite(cut.beta)) {
      logger.error('Invalid cut beta:', cut.beta);
      throw new Error('Invalid cut object: invalid beta');
    }

    // Extend 2D cut to 3D by adding z coefficient of 0
    const alpha3D = cut.alpha.length === 2
      ? [...cut.alpha, 0]
      : cut.alpha.slice(0, 3);

    // Validate alpha3D
    if (alpha3D.length !== 3) {
      logger.error('Alpha3D does not have 3 components:', alpha3D);
      throw new Error('Failed to create 3D alpha vector');
    }

    // Check for invalid coefficients
    for (let i = 0; i < alpha3D.length; i++) {
      if (typeof alpha3D[i] !== 'number' || !isFinite(alpha3D[i])) {
        logger.error(`Invalid coefficient at index ${i}:`, alpha3D[i]);
        throw new Error(`Invalid coefficient in alpha3D at index ${i}`);
      }
    }

    const plane = createPlaneFromInequality(alpha3D, cut.beta);

    // Create main visualization with higher opacity for better visibility
    const visualization = createCuttingPlaneVisualization(alpha3D, cut.beta, size, 0x64b5f6, 0.6);

    // Create wireframe with slightly darker blue
    const wireframe = createCuttingPlaneWireframe(alpha3D, cut.beta, size, 0x42a5f5);

    // Validate that visualization has the required properties
    if (!visualization || !visualization.position) {
      logger.error('Visualization missing position property:', visualization);
      throw new Error('Visualization object is invalid');
    }

    if (!visualization.userData) {
      visualization.userData = {};
    }

    if (!visualization.userData.normal) {
      logger.warn('Adding missing normal to visualization userData');
      visualization.userData.normal = new THREE.Vector3(alpha3D[0], alpha3D[1], alpha3D[2]).normalize();
    }

    // Add some visual enhancements
    if (animated) {
      try {
        // Add subtle pulsing animation
        const material = visualization.material as THREE.MeshStandardMaterial;
        const originalOpacity = material.opacity;

        const animate = () => {
          const time = Date.now() * 0.003;
          material.opacity = originalOpacity + Math.sin(time) * 0.05;
          requestAnimationFrame(animate);
        };
        animate();
      } catch (animationError) {
        logger.warn('Failed to add animation to cutting plane:', animationError);
      }
    }

    return { plane, visualization, wireframe };

  } catch (error) {
    logger.error('Error in createCuttingPlaneFromCut:', error);
    logger.error('Cut data:', cut);

    // Create fallback objects with modern styling
    try {
      const fallbackPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);

      const fallbackGeometry = new THREE.PlaneGeometry(size, size);
      const fallbackMaterial = new THREE.MeshStandardMaterial({
        color: 0x3b82f6,
        transparent: true,
        opacity: 0.3,
        side: THREE.DoubleSide,
        emissive: new THREE.Color(0x3b82f6).multiplyScalar(0.1)
      });

      const fallbackVisualization = new THREE.Mesh(fallbackGeometry, fallbackMaterial);
      fallbackVisualization.position.set(0, 0, 0);
      fallbackVisualization.userData = { normal: new THREE.Vector3(0, 0, 1) };

      const fallbackWireframeGeometry = new THREE.WireframeGeometry(fallbackGeometry);
      const fallbackWireframeMaterial = new THREE.LineBasicMaterial({
        color: 0x42a5f5,
        transparent: true,
        opacity: 0.8
      });
      const fallbackWireframe = new THREE.LineSegments(fallbackWireframeGeometry, fallbackWireframeMaterial);

      return {
        plane: fallbackPlane,
        visualization: fallbackVisualization,
        wireframe: fallbackWireframe
      };
    } catch (fallbackError) {
      logger.error('Failed to create fallback objects:', fallbackError);
      throw new Error('Complete failure in createCuttingPlaneFromCut');
    }
  }
}

/**
 * Get the equation string for a cutting plane
 */
export function getCuttingPlaneEquation(alpha: number[], beta: number): string {
  const terms: string[] = []
  const variables = ['x', 'y', 'z']

  for (let i = 0; i < Math.min(alpha.length, 3); i++) {
    const coeff = alpha[i]
    if (coeff !== 0) {
      const sign = coeff > 0 && terms.length > 0 ? '+' : ''
      const coeffStr = Math.abs(coeff) === 1 ? '' : Math.abs(coeff).toString()
      const term = `${sign}${coeff < 0 ? '-' : ''}${coeffStr}${variables[i]}`
      terms.push(term)
    }
  }

  return terms.length > 0 ? `${terms.join('')} ≤ ${beta}` : `0 ≤ ${beta}`
}

/**
 * Transform coordinates between different coordinate systems
 */
export class CoordinateTransform {
  private transform: THREE.Matrix4
  private inverseTransform: THREE.Matrix4

  constructor(
    translation: THREE.Vector3 = new THREE.Vector3(0, 0, 0),
    rotation: THREE.Euler = new THREE.Euler(0, 0, 0),
    scale: THREE.Vector3 = new THREE.Vector3(1, 1, 1)
  ) {
    this.transform = new THREE.Matrix4()
    this.transform.compose(translation, new THREE.Quaternion().setFromEuler(rotation), scale)

    this.inverseTransform = new THREE.Matrix4()
    this.inverseTransform.copy(this.transform).invert()
  }

  /**
   * Transform a point from mathematical coordinates to Three.js coordinates
   */
  transformPoint(point: THREE.Vector3): THREE.Vector3 {
    return point.clone().applyMatrix4(this.transform)
  }

  /**
   * Transform a point from Three.js coordinates to mathematical coordinates
   */
  inverseTransformPoint(point: THREE.Vector3): THREE.Vector3 {
    return point.clone().applyMatrix4(this.inverseTransform)
  }

  /**
   * Transform a plane from mathematical coordinates to Three.js coordinates
   */
  transformPlane(plane: THREE.Plane): THREE.Plane {
    const transformedPlane = plane.clone()
    // Extract the normal matrix from the transform
    const normalMatrix = new THREE.Matrix3().getNormalMatrix(this.transform)
    transformedPlane.applyMatrix4(this.transform, normalMatrix)
    return transformedPlane
  }
}

/**
 * Create a half-space mesh from a plane
 * 
 * @param plane - The cutting plane
 * @param invertSide - If true, keeps the side opposite to the plane normal
 * @returns THREE.Mesh representing the half-space
 */
export function createHalfSpaceMesh(plane: THREE.Plane, material: THREE.Material, invertSide: boolean = false): THREE.Mesh {
  // Create a large box that represents the half-space
  const size = 100;

  const pointOnPlane = new THREE.Vector3();
  plane.projectPoint(new THREE.Vector3(0, 0, 0), pointOnPlane);

  const normal = plane.normal.clone();
  if (invertSide) {
    normal.negate();
  }

  const boxGeometry = new THREE.BoxGeometry(size, size, size);
  // Use the same material as the target mesh to ensure the new faces get the correct material.
  const boxMesh = new THREE.Mesh(boxGeometry, material);

  const offset = size / 2;
  boxMesh.position.copy(pointOnPlane).add(normal.clone().multiplyScalar(offset));

  const rotationMatrix = new THREE.Matrix4();
  const up = Math.abs(normal.dot(new THREE.Vector3(0, 1, 0))) < 0.99
    ? new THREE.Vector3(0, 1, 0)
    : new THREE.Vector3(1, 0, 0);

  rotationMatrix.lookAt(normal, new THREE.Vector3(0, 0, 0), up);
  boxMesh.quaternion.setFromRotationMatrix(rotationMatrix);

  return boxMesh;
}

export function createMeshFromInequalities(
  inequalities: Inequality[],
  opts?: { equalityPlaneFallback?: boolean; errorFallbackInvisible?: boolean }
): THREE.Mesh {
  const equalityPlaneFallback = opts?.equalityPlaneFallback !== false

  if (!inequalities || inequalities.length === 0) {
    logger.warn('No inequalities provided, creating default cube');
    if (opts?.errorFallbackInvisible) {
      const g = new THREE.BufferGeometry()
      const m = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
      return new THREE.Mesh(g, m)
    }
    return createDefaultCube();
  }

  try {
    // Equality-aware fast path: if an equality plane is implied, build its bounded polygon directly
    const eqEarly = detectEqualityPlane(inequalities)
    if (eqEarly) {
      const poly = buildPolygonOnEqualityPlane(eqEarly, inequalities)
      if (poly) {
        const material = new THREE.MeshPhysicalMaterial({
          color: 0x4169e1,
          metalness: 0.0,
          roughness: 0.8,
          transmission: 0.0,
          transparent: true,
          opacity: 0.45,
          side: THREE.DoubleSide,
          envMapIntensity: 0.0,
          clearcoat: 0.0,
          clearcoatRoughness: 1.0,
          ior: 1.0,
        })
        return new THREE.Mesh(poly, material)
      }
      // Fallback when polygon building fails
      if (equalityPlaneFallback) {
        return createCuttingPlaneVisualization(eqEarly.alpha, eqEarly.beta, 8, 0x64b5f6, 0.35)
      } else {
        // Return an invisible placeholder mesh to avoid clutter
        const g = new THREE.BufferGeometry()
        const m = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
        return new THREE.Mesh(g, m)
      }
    }

    // Step 1: Create geometry from inequalities using half-space intersection
    const geometry = createPolyhedronFromInequalities(inequalities);

    // Step 2: Verify geometry has vertices
    if (!geometry.attributes.position || geometry.attributes.position.count === 0) {
      logger.warn('Generated geometry has no vertices. Attempting equality-plane fallback...');
      const eq = detectEqualityPlane(inequalities)
      if (eq) {
        const poly = buildPolygonOnEqualityPlane(eq, inequalities)
        if (poly) {
          const material = new THREE.MeshPhysicalMaterial({
            color: 0x4169e1,
            metalness: 0.0,
            roughness: 0.8,
            transmission: 0.0,
            transparent: true,
            opacity: 0.45,
            side: THREE.DoubleSide,
            envMapIntensity: 0.0,
            clearcoat: 0.0,
            clearcoatRoughness: 1.0,
            ior: 1.0,
          })
          const mesh = new THREE.Mesh(poly, material)
          logger.warn('Using equality-constrained polygon mesh for visualization')
          return mesh
        }
        if (equalityPlaneFallback) {
          const sizeHint = 8
          const planeMesh = createCuttingPlaneVisualization(eq.alpha, eq.beta, sizeHint, 0x64b5f6, 0.35)
          logger.warn('Using equality-plane fallback mesh for visualization')
          return planeMesh
        } else {
          // Invisible placeholder
          const g = new THREE.BufferGeometry()
          const m = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
          return new THREE.Mesh(g, m)
        }
      }
      logger.warn('No equality-plane detected; returning invisible placeholder')
      const g = new THREE.BufferGeometry()
      const m = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
      return new THREE.Mesh(g, m)
    }

    // Step 3: Create material balancing preview visibility and interior access
    const material = new THREE.MeshPhysicalMaterial({
      color: 0x4169e1,        // Royal blue sapphire - elegant and professional
      metalness: 0.0,         // No metallic effect
      roughness: 0.8,         // Matte surface to reduce reflections
      transmission: 0.0,      // No glass transmission effect
      transparent: true,
      opacity: 0.4,           // Lower opacity for interior visibility
      side: THREE.DoubleSide,
      envMapIntensity: 0.0,   // No reflections
      clearcoat: 0.0,         // No clear coating
      clearcoatRoughness: 1.0, // Maximum roughness
      ior: 1.0,               // No refraction
      emissive: 0x001133,     // Keep subtle blue inner glow
      depthWrite: true,       // Enable depth writing for proper blending
      alphaTest: 0.01,        // Small alpha test for better transparency
    });

    // Step 4: Create mesh
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;

    geometry.computeBoundingBox();

    return mesh;
  } catch (error) {
    logger.error('Error creating mesh from inequalities:', error);
    if (opts?.errorFallbackInvisible) {
      const g = new THREE.BufferGeometry()
      const m = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
      return new THREE.Mesh(g, m)
    }
    return createDefaultCube();
  }
}

/**
 * Detect an equality plane implied by the presence of a pair of opposite constraints:
 *   a·x ≤ b  and  (-a)·x ≤ -b  ⇒  a·x = b
 */
function detectEqualityPlane(inequalities: Inequality[]): { alpha: number[]; beta: number } | null {
  const tol = 1e-9
  const n = inequalities.length
  const norm = (v: number[]) => {
    const s = Math.sqrt(v.reduce((acc, x) => acc + x * x, 0)) || 1
    return v.map((x) => x / s)
  }
  const isOpposite = (a: number[], b: number[]) => a.length === b.length && a.every((ai, i) => Math.abs(ai + b[i]) < tol)
  for (let i = 0; i < n; i++) {
    const a1 = inequalities[i].alpha
    const b1 = inequalities[i].beta
    for (let j = i + 1; j < n; j++) {
      const a2 = inequalities[j].alpha
      const b2 = inequalities[j].beta
      if (isOpposite(norm(a1), norm(a2)) && Math.abs(b1 + b2) < 1e-6) {
        // Equality detected: return the positive-orientation version (prefer non-negative beta if possible)
        return b1 >= 0 ? { alpha: ensureThreeDimensions(a1), beta: b1 } : { alpha: ensureThreeDimensions(a2).map((v) => v * -1), beta: -b2 }
      }
    }
  }
  return null
}

/**
 * Build the bounded polygon on the equality plane by enumerating all intersections of
 *   {eq} + any two other inequalities. A valid vertex must satisfy all inequalities.
 */
function buildPolygonOnEqualityPlane(eq: { alpha: number[]; beta: number }, inequalities: Inequality[]): THREE.BufferGeometry | null {
  const eqIneq: Inequality = { alpha: ensureThreeDimensions(eq.alpha), beta: eq.beta }
  const others = inequalities.filter((q) => !(
    arraysAlmostOpposite(ensureThreeDimensions(q.alpha), eqIneq.alpha) && Math.abs(q.beta + eqIneq.beta) < 1e-6
  ))

  const candidates: THREE.Vector3[] = []
  for (let i = 0; i < others.length; i++) {
    for (let j = i + 1; j < others.length; j++) {
      const A = [
        eqIneq.alpha.slice(0, 3),
        ensureThreeDimensions(others[i].alpha).slice(0, 3),
        ensureThreeDimensions(others[j].alpha).slice(0, 3),
      ]
      const b = [eqIneq.beta, others[i].beta, others[j].beta]
      const v = solveLinearSystem3x3(A, b)
      if (v && isValidVertex(v, inequalities)) candidates.push(v)
    }
  }
  const unique = removeDuplicateVertices(candidates)
  if (unique.length >= 3) {
    // Sort vertices on the equality plane and triangulate as a simple polygon (fan)
    const normal = new THREE.Vector3(eqIneq.alpha[0] || 0, eqIneq.alpha[1] || 0, eqIneq.alpha[2] || 0).normalize()
    const center = unique.reduce((acc, v) => acc.add(v.clone()), new THREE.Vector3()).multiplyScalar(1 / unique.length)
    // Choose a tangent vector not parallel to normal
    const temp = Math.abs(normal.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0)
    const tangent = temp.clone().sub(normal.clone().multiplyScalar(temp.dot(normal))).normalize()
    const binormal = new THREE.Vector3().crossVectors(normal, tangent).normalize()

    const withAngles = unique.map((v) => {
      const d = v.clone().sub(center)
      const x = d.dot(tangent)
      const y = d.dot(binormal)
      const angle = Math.atan2(y, x)
      return { v, angle }
    })
    withAngles.sort((a, b) => a.angle - b.angle)
    const verts = withAngles.map((p) => p.v)

    const positions: number[] = []
    verts.forEach((p) => positions.push(p.x, p.y, p.z))
    const indices: number[] = []
    for (let i = 1; i < verts.length - 1; i++) {
      indices.push(0, i, i + 1)
    }

    const geom = new THREE.BufferGeometry()
    geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
    geom.setIndex(indices)
    geom.computeVertexNormals()
    geom.computeBoundingBox()
    return geom
  }
  return null
}

function arraysAlmostOpposite(a: number[], b: number[], tol: number = 1e-9): boolean {
  if (a.length !== b.length) return false
  const n1 = new THREE.Vector3(a[0] || 0, a[1] || 0, a[2] || 0).normalize()
  const n2 = new THREE.Vector3(b[0] || 0, b[1] || 0, b[2] || 0).normalize()
  return n1.dot(n2) < -1 + tol
}

/**
 * Create a default visible cube for fallback cases
 */
function createDefaultCube(): THREE.Mesh {
  const geometry = new THREE.BoxGeometry(4, 4, 4);
  const material = new THREE.MeshPhysicalMaterial({
    color: 0x4169e1,        // Match the royal blue sapphire
    metalness: 0.0,         // No metallic effect
    roughness: 0.8,         // Matte surface to reduce reflections
    transmission: 0.0,      // No glass transmission effect
    transparent: true,
    opacity: 0.4,           // Lower opacity for interior visibility
    side: THREE.DoubleSide,
    envMapIntensity: 0.0,   // No reflections
    clearcoat: 0.0,         // No clear coating
    clearcoatRoughness: 1.0, // Maximum roughness
    ior: 1.0,               // No refraction
    emissive: 0x001133,     // Subtle blue glow
    depthWrite: true,       // Enable depth writing for proper blending
    alphaTest: 0.01,        // Small alpha test for better transparency
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.position.set(2, 2, 2);  // Position at camera target

  logger.log('Created default fallback cube');
  return mesh;
}

/**
 * Validate that all vertices of a generated geometry satisfy the given inequalities.
 * Returns an object with validity flag and number of violations found.
 */
export function validateGeometryWithInequalities(
  geometry: THREE.BufferGeometry,
  inequalities: Inequality[],
  tolerance: number = 1e-4
): { valid: boolean; violations: number } {
  if (!geometry.attributes.position) {
    logger.warn('validateGeometryWithInequalities: geometry has no position attribute');
    return { valid: false, violations: Infinity };
  }

  const positions = geometry.attributes.position as THREE.BufferAttribute;
  let violations = 0;
  const posVec = new THREE.Vector3();

  for (let i = 0; i < positions.count; i++) {
    posVec.set(positions.getX(i), positions.getY(i), positions.getZ(i));

    for (const { alpha, beta } of inequalities) {
      const val = (alpha[0] || 0) * posVec.x + (alpha[1] || 0) * posVec.y + (alpha[2] || 0) * posVec.z;
      if (val > beta + tolerance) {
        violations++;
        if (violations > 10) {
          // Early exit after many violations
          return { valid: false, violations };
        }
        break;
      }
    }
  }

  return { valid: violations === 0, violations };
}

// ---------- Degeneracy helpers for BnB visualization ----------
export function createLineSegmentBetween(a: THREE.Vector3, b: THREE.Vector3, color: number = 0xffb703, width: number = 2): THREE.LineSegments {
  const geometry = new THREE.BufferGeometry()
  const positions = new Float32Array([a.x, a.y, a.z, b.x, b.y, b.z])
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  const material = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.95, linewidth: width as any, depthTest: false })
  const lines = new THREE.LineSegments(geometry, material)
  lines.renderOrder = 150
  return lines
}

export function createPointMarker(p: THREE.Vector3, color: number = 0xff6b35, radius: number = 0.06): THREE.Mesh {
  const geom = new THREE.SphereGeometry(radius, 16, 16)
  const mat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1.0, depthTest: false, depthWrite: false })
  const m = new THREE.Mesh(geom, mat)
  m.position.copy(p)
  m.renderOrder = 160
  return m
}

/**
 * Calculates and creates a mesh representing the intersection (cross-section) 
 * between a geometry and a plane.
 */
export function createIntersectionMesh(
  mesh: THREE.Mesh,
  plane: THREE.Plane,
  color: number = 0xff4500
): THREE.Mesh | null {
  if (!mesh || !mesh.geometry || !plane) return null;

  const geometry = mesh.geometry;
  if (!geometry.attributes.position) return null;

  const positions = geometry.attributes.position;
  const indices = geometry.index;
  const intersectionPoints: THREE.Vector3[] = [];

  mesh.updateMatrixWorld();
  const matrix = mesh.matrixWorld;

  const vA = new THREE.Vector3();
  const vB = new THREE.Vector3();
  const vC = new THREE.Vector3();

  const count = indices ? indices.count : positions.count;
  const EPS = 1e-5;

  for (let i = 0; i < count; i += 3) {
    let a, b, c;
    if (indices) {
      a = indices.getX(i);
      b = indices.getX(i + 1);
      c = indices.getX(i + 2);
    } else {
      a = i;
      b = i + 1;
      c = i + 2;
    }

    vA.fromBufferAttribute(positions, a).applyMatrix4(matrix);
    vB.fromBufferAttribute(positions, b).applyMatrix4(matrix);
    vC.fromBufferAttribute(positions, c).applyMatrix4(matrix);

    const dA = plane.distanceToPoint(vA);
    const dB = plane.distanceToPoint(vB);
    const dC = plane.distanceToPoint(vC);

    // Check intersections on edges
    if (dA * dB < 0) {
      const t = dA / (dA - dB);
      intersectionPoints.push(vA.clone().lerp(vB, t));
    }
    if (dB * dC < 0) {
      const t = dB / (dB - dC);
      intersectionPoints.push(vB.clone().lerp(vC, t));
    }
    if (dC * dA < 0) {
      const t = dC / (dC - dA);
      intersectionPoints.push(vC.clone().lerp(vA, t));
    }

    // Include vertices exactly on plane
    if (Math.abs(dA) < EPS) intersectionPoints.push(vA.clone());
    if (Math.abs(dB) < EPS) intersectionPoints.push(vB.clone());
    if (Math.abs(dC) < EPS) intersectionPoints.push(vC.clone());
  }

  if (intersectionPoints.length < 3) return null;

  // Deduplicate
  const uniquePoints: THREE.Vector3[] = [];
  const seen = new Set<string>();
  const resolution = 10000; // precision

  for (const p of intersectionPoints) {
    const key = `${Math.round(p.x * resolution)},${Math.round(p.y * resolution)},${Math.round(p.z * resolution)}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniquePoints.push(p);
    }
  }

  if (uniquePoints.length < 3) return null;

  // Project to 2D for sorting (Convex Hull logic)
  // We assume the intersection is convex because the polyhedron is convex.
  const normal = plane.normal.clone().normalize();
  let u = new THREE.Vector3(0, 1, 0);
  if (Math.abs(normal.dot(u)) > 0.99) u = new THREE.Vector3(1, 0, 0);
  const v = new THREE.Vector3().crossVectors(normal, u).normalize();
  u = new THREE.Vector3().crossVectors(v, normal).normalize();

  const center = new THREE.Vector3();
  for (const p of uniquePoints) center.add(p);
  center.divideScalar(uniquePoints.length);

  uniquePoints.sort((a, b) => {
    const da = new THREE.Vector3().subVectors(a, center);
    const db = new THREE.Vector3().subVectors(b, center);
    const angA = Math.atan2(da.dot(v), da.dot(u));
    const angB = Math.atan2(db.dot(v), db.dot(u));
    return angA - angB;
  });

  // Build Mesh
  const geo = new THREE.BufferGeometry().setFromPoints(uniquePoints);
  const meshIndices: number[] = [];
  for (let i = 1; i < uniquePoints.length - 1; i++) {
    meshIndices.push(0, i, i + 1);
  }
  geo.setIndex(meshIndices);
  geo.computeVertexNormals();

  // "Mathematical Highlight" Style
  // 1. Bright Gold/Yellow fill to contrast with Blue body and Teal plane
  // 2. Depth Test TRUE to ensure it looks like it's INSIDE the object, not a sticker on screen
  // 3. Polygon Offset to draw on top of the cutting plane surface without z-fighting
  const mat = new THREE.MeshBasicMaterial({
    color: 0xffd700, // Gold
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.6,    // Visible but see-through
    depthTest: true, // CRITICAL: Respect 3D depth
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2, // Pull slightly forward visually
    polygonOffsetUnits: 0.1
  });

  const intersectionMesh = new THREE.Mesh(geo, mat);

  // Thick bright outline
  const edges = new THREE.EdgesGeometry(geo);
  const edgeMat = new THREE.LineBasicMaterial({
    color: 0xffffff, // Pure white outline
    linewidth: 2,
    transparent: true,
    opacity: 1.0,
    depthTest: true, // Match mesh depth
    depthWrite: false
  });
  const wire = new THREE.LineSegments(edges, edgeMat);
  // Ensure wire renders on top of the fill
  wire.renderOrder = 61;
  intersectionMesh.add(wire);

  return intersectionMesh;
}
/**
 * Create a cross-section (slice) of the polyhedron at the cut plane.
 * Returns a mesh representing the polygon intersection, or null if no intersection.
 */
export function createCutCrossSection(
  parentInequalities: Inequality[],
  cutAlpha: number[],
  cutBeta: number
): THREE.Group | null {
  try {
    // 1. Define the cut plane as two inequalities: alpha*x <= beta AND alpha*x >= beta
    // This effectively restricts the region to the plane itself.
    // However, for vertex generation, we can just add the plane equation to the system
    // or use a specialized intersection logic.
    // A robust way using existing tools:
    // The vertices of the slice are the vertices of the polyhedron { P intersect H }
    // where H is the hyperplane.
    // We can generate vertices for { P, alpha*x <= beta, alpha*x >= beta }
    // But our vertex generator might struggle with equality constraints if not handled explicitly.

    // Alternative: Find edges of P that intersect H.
    // Vertices of P are v1..vn. Edges are (vi, vj).
    // Intersection point p = intersection(edge, H).
    // Collect all such points.

    // Let's use the "add equality constraint" approach with our robust vertex generator.
    // We add alpha*x <= beta and -alpha*x <= -beta.
    const sliceConstraints = [
      ...parentInequalities,
      { alpha: cutAlpha, beta: cutBeta },
      { alpha: cutAlpha.map(a => -a), beta: -cutBeta }
    ];

    const allVertices = generateVerticesFromConstraints(sliceConstraints);
    const vertices = removeDuplicateVertices(allVertices);

    if (vertices.length < 3) return null;

    // Sort vertices to form a polygon
    // Project to 2D on the plane basis
    const normal = new THREE.Vector3(cutAlpha[0], cutAlpha[1], cutAlpha[2]).normalize();

    // Find a center point
    const center = new THREE.Vector3();
    vertices.forEach(v => center.add(v));
    center.divideScalar(vertices.length);

    // Basis vectors
    let tangent = new THREE.Vector3();
    if (Math.abs(normal.y) < 0.9 && Math.abs(normal.z) < 0.9) {
      tangent.set(0, 1, 0);
    } else {
      tangent.set(1, 0, 0);
    }
    tangent.crossVectors(normal, tangent).normalize(); // Make tangent orthogonal to normal
    // Re-cross to ensure tangent is strictly orthogonal (first cross might be approximate if input wasn't perfect)
    // Actually, cross(normal, arbitrary) gives a vector orthogonal to normal.
    // Then binormal = cross(normal, tangent).
    const binormal = new THREE.Vector3().crossVectors(normal, tangent).normalize();

    // Sort
    vertices.sort((a, b) => {
      const vecA = new THREE.Vector3().subVectors(a, center);
      const vecB = new THREE.Vector3().subVectors(b, center);
      const angleA = Math.atan2(vecA.dot(binormal), vecA.dot(tangent));
      const angleB = Math.atan2(vecB.dot(binormal), vecB.dot(tangent));
      return angleA - angleB;
    });

    // Create Mesh
    const shape = new THREE.Shape();
    // Project 3D vertices to 2D plane coordinates for Shape
    vertices.forEach((v, i) => {
      const vec = new THREE.Vector3().subVectors(v, center);
      const x = vec.dot(tangent);
      const y = vec.dot(binormal);
      if (i === 0) shape.moveTo(x, y);
      else shape.lineTo(x, y);
    });
    shape.closePath();

    const geometry = new THREE.ShapeGeometry(shape);

    // The geometry is in 2D XY plane centered at 0. We need to transform it back to 3D.
    // Position: center
    // Rotation: align Z axis to normal, X to tangent, Y to binormal
    const mesh = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({
      color: 0x22d3ee,
      transparent: true,
      opacity: 0.4, // Higher opacity for the slice
      side: THREE.DoubleSide,
      emissive: 0x22d3ee,
      emissiveIntensity: 0.4,
      depthWrite: false
    }));

    // Construct transformation matrix
    // Columns: tangent, binormal, normal, center
    const matrix = new THREE.Matrix4();
    matrix.makeBasis(tangent, binormal, normal);
    matrix.setPosition(center);

    // Apply matrix to mesh (or geometry)
    // Applying to mesh.matrix requires matrixAutoUpdate=false
    mesh.applyMatrix4(matrix);

    // Add wireframe outline
    const edges = new THREE.EdgesGeometry(geometry);
    const wireframe = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.8,
      linewidth: 2
    }));
    // Wireframe needs same transform
    // Note: edges geometry is already baked? No, it's based on input geometry.
    // If we applied matrix to mesh, children inherit it? No, applyMatrix4 transforms vertices of geometry if applied to geometry,
    // or sets local matrix if applied to mesh?
    // mesh.applyMatrix4 modifies position/rotation/scale.
    // So if we add wireframe as child, it will inherit.
    mesh.add(wireframe);

    const group = new THREE.Group();
    group.add(mesh);

    return group;

  } catch (e) {
    logger.warn('Failed to create cut cross section:', e);
    return null;
  }
}
